import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useSessionStorage } from "usehooks-ts";
import type { ToolkitCollection } from "../../../clients/arcade/arcade";
import { buildToolkitCollection } from "../../../clients/arcade/toolkit-collection";
import { toolkitCollectionKeys } from "../../../hooks/query-keys";
import {
  buildDefaultToolkitSelection,
  DEFAULT_TOOLKIT_NAMES,
} from "../../../utils/default-tool-selection";
import {
  filterHiddenToolkits,
  HIDDEN_TOOLKITS,
} from "../../../utils/filter-hidden-toolkits";

const MAX_TOOLS_FOR_AI = 64;
const STORAGE_KEY_PREFIX = "arcade:toolkit-selection";

type ChatToolkitsSelection = {
  selectedToolkits: string[];
  selectedTools: string[];
};

type ChatToolkitsState = {
  toolkitCollection: ToolkitCollection | undefined;
  isLoading: boolean;
  isError: boolean;
  isWaitingForToolkits: boolean;
  selectedToolkits: string[];
  selectedTools: string[];
  toolsForAI: string[];
  maxToolsForAI: number;
  defaultToolFqns: string[];
  onSelectionChange: (next: ChatToolkitsSelection) => void;
};

function reconcile(
  persisted: ChatToolkitsSelection,
  collection: ToolkitCollection
): ChatToolkitsSelection | null {
  const toolkits = persisted.selectedToolkits.filter((name) =>
    collection.toolkitMap.has(name)
  );
  const tools = persisted.selectedTools.filter((fqn) => {
    const toolkitName = collection.toolToToolkitMap.get(fqn);
    return toolkitName != null && collection.toolkitMap.has(toolkitName);
  });

  return toolkits.length === 0 && tools.length === 0
    ? null
    : { selectedToolkits: toolkits, selectedTools: tools };
}

export function useChatToolkitsState({
  baseUrl,
  organizationId,
  projectId,
  viewerId,
  defaultToolkitNames = DEFAULT_TOOLKIT_NAMES,
}: {
  baseUrl: string;
  organizationId: string | null;
  projectId: string | null;
  viewerId: string;
  defaultToolkitNames?: ReadonlySet<string> | readonly string[];
}): ChatToolkitsState {
  const hasContext = Boolean(organizationId && projectId);
  const storageKey = `${STORAGE_KEY_PREFIX}:${organizationId ?? ""}:${projectId ?? ""}:${viewerId}`;

  const [persistedSelection, setPersistedSelection] =
    useSessionStorage<ChatToolkitsSelection | null>(storageKey, null);

  const {
    data: toolkitCollection,
    isLoading,
    isError,
  } = useQuery({
    queryKey: toolkitCollectionKeys.collection({
      organizationId,
      projectId,
      viewerId,
    }),
    queryFn: async () => {
      const url = new URL(
        `${baseUrl}/api/engine/orgs/${organizationId}/projects/${projectId}/tools`
      );
      url.searchParams.set("limit", "25000");

      if (viewerId) {
        url.searchParams.set("user_id", viewerId);
      }

      const res = await fetch(url.toString(), { credentials: "include" });
      if (!res.ok) {
        throw new Error(`Failed to fetch tools: ${res.status}`);
      }

      const json = await res.json();
      return buildToolkitCollection(json.items);
    },
    select: (data) => filterHiddenToolkits(data, HIDDEN_TOOLKITS),
    enabled: hasContext,
    staleTime: 5 * 60 * 1000,
  });

  const defaultSelection = useMemo<ChatToolkitsSelection>(() => {
    if (!toolkitCollection) {
      return { selectedToolkits: [], selectedTools: [] };
    }

    const preferredToolkitNames = Array.isArray(defaultToolkitNames)
      ? defaultToolkitNames
      : Array.from(defaultToolkitNames);

    return buildDefaultToolkitSelection(
      toolkitCollection,
      preferredToolkitNames
    );
  }, [toolkitCollection, defaultToolkitNames]);

  const selection = useMemo(() => {
    if (!persistedSelection) {
      return defaultSelection;
    }

    if (!toolkitCollection) {
      return persistedSelection;
    }

    return reconcile(persistedSelection, toolkitCollection) ?? defaultSelection;
  }, [defaultSelection, persistedSelection, toolkitCollection]);

  return useMemo(() => {
    const toolsForAI = selection.selectedTools.slice(0, MAX_TOOLS_FOR_AI);
    const isWaitingForToolkits = hasContext && !toolkitCollection && !isError;

    return {
      toolkitCollection,
      isLoading,
      isError,
      isWaitingForToolkits,
      selectedToolkits: selection.selectedToolkits,
      selectedTools: selection.selectedTools,
      toolsForAI,
      maxToolsForAI: MAX_TOOLS_FOR_AI,
      defaultToolFqns: defaultSelection.selectedTools,
      onSelectionChange: setPersistedSelection,
    };
  }, [
    toolkitCollection,
    isLoading,
    isError,
    hasContext,
    selection,
    defaultSelection,
    setPersistedSelection,
  ]);
}
